import { memo, useMemo } from 'react';
import { BarChart3, Target } from 'lucide-react';
import { useBudgetPreview } from '../../contexts/BudgetPreviewContext';
import { calculateProjectedImpact } from '../../utils/budgetTiers';

export default memo(function ProjectedImpactSidebar() {
  const { preview } = useBudgetPreview();
  const { dailyBudget, floorPrice, claimType } = preview;

  const hasBudget = dailyBudget > 0 && floorPrice > 0;
  const { dailyLeads, monthlyLeads } = useMemo(
    () => calculateProjectedImpact(dailyBudget, floorPrice),
    [dailyBudget, floorPrice],
  );
  const estMarketBidLow = floorPrice;
  const estMarketBidHigh = useMemo(() => Math.round(floorPrice * 1.1), [floorPrice]);

  return (
    <div className="sticky top-6">
      <div className="rounded-xl overflow-hidden">
        {/* Header */}
        <div className="bg-brand-surface p-6">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-accent" />
            <h3 className="text-lg font-semibold text-neutral-light">Projected Impact</h3>
          </div>
          <p className="text-sm text-text-muted">
            Based on your budget and current market conditions
            {claimType ? ` for ${claimType}` : ''}.
          </p>

          {/* Hero Stat */}
          {hasBudget ? (
            <div className="mt-6 bg-accent/10 border border-accent/20 shadow-neon-glow rounded-xl p-5 text-center">
              <p className="text-xs font-semibold uppercase tracking-wider text-accent/70">
                Est. Monthly Volume
              </p>
              <p className="text-5xl font-bold text-neutral-light mt-1">{monthlyLeads}</p>
              <p className="text-sm text-accent/70 mt-1">leads/mo</p>
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center py-8 text-center">
              <div className="p-3 rounded-full bg-white/5 mb-3">
                <Target className="w-8 h-8 text-gray-500" />
              </div>
              <p className="text-sm text-text-muted">
                Set a budget to see your
                <br />
                projected lead volume.
              </p>
            </div>
          )}
        </div>

        {/* Metrics */}
        {hasBudget && (
          <div className="bg-brand-bg p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-muted">Daily Leads</span>
              <span className="text-sm font-semibold text-neutral-light">~{dailyLeads}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-muted">Est. Market Bid</span>
              <span className="text-sm font-semibold text-neutral-light">
                ${estMarketBidLow} - ${estMarketBidHigh}
              </span>
            </div>
            <div className="border-t border-gray-700/50" />
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-muted">Daily Spend Limit</span>
              <span className="text-sm font-bold text-accent">${dailyBudget}</span>
            </div>

            {/* Pricing Note */}
            <div className="pt-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-kpi-retainer mb-1.5">
                Pricing Note
              </p>
              <p className="text-xs text-text-muted leading-relaxed">
                This budget sets your <span className="text-neutral-light font-medium">spending limit</span>.
                In the next step, you will set your{' '}
                <span className="text-neutral-light font-medium">Max Bid</span> (Price Per Lead) for
                specific locations.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
})
